<template>
    <v-flex xs12>
      <v-card>
        <v-flex xs12 pt-4 px-4 v-if="err">
          <v-alert type="error" :value="true">{{err}}</v-alert>
        </v-flex>
        <v-card-title class="pb-3 px-2 layout row wrap">
          <v-btn v-if="!isLocalAdmin" class="primary my-0 mx-3" @click='$router.push({ name: "webShopDetail" })'>Create New Web Shop Resource</v-btn>
          <v-checkbox
            color="primary"
            class="mx-1 mt-0"
            :label="$lang.labels.showActive"
            v-model="showActive"
            hide-details
          />
          <v-spacer/>
          <v-autocomplete
            v-if="!isLocalAdmin"
            class="min-w max-w mx-3 pb-2"
            :label="$lang.actions.selectCompany"
            :items="mappedCompanies"
            :loading="companyLoading"
            v-model="selectedCompany"
            hide-details
          />
          <v-text-field
            v-if="show"
            class="min-w max-w mx-3 pb-2"
            v-model="search"
            append-icon="search"
            :label="$lang.labels.search"
            hide-details
          />
        </v-card-title>
        <v-data-table
          v-if="show"
          :headers="headers"
          :items="showActive ? activeWebShops : allWebShops"
          :search="search"
          :loading="loading"
          :rows-per-page-items="[50, 100, 250, 500, { text:'All' , value: -1 }]"
        >
          <v-progress-linear height="6" slot="progress" color="primary" indeterminate />
          <template slot="items" slot-scope="props">
            <td class="min-w">{{ props.item.name }}</td>
            <td class="min-w">{{ props.item.webShopResourceExternalId }}</td>
            <td class="text-xs-left min-w">{{ props.item.companyName }}</td>
            <td class="text-xs-left">
              {{ props.item.pendingOrders }}
              <v-btn fab small class="primary" outline @click="showOrder(props.item)"><v-icon class="primary--text">arrow_forward</v-icon></v-btn>
            </td>
            <td class="text-xs-left">
              <v-btn
                flat class="primary--text mx-0"
                @click="showModal(props.item)"
                v-if="props.item.lastPendingShortOrderId || props.item.lastPendingOrderId">
                {{ props.item.lastPendingShortOrderId || props.item.lastPendingOrderId }}
              </v-btn>
            </td>
            <td class="text-xs-left">{{ props.item.status }}</td>
            <td class="text-xs-left">{{ formatDate(props.item.createdOn) }}</td>
            <td class="text-xs-left min-w-lg">
              <v-btn class="primary mx-0 elevation-1" @click="$router.push({ name: 'webShopEdit', params: { id: props.item.id }})">{{$lang.actions.edit}}</v-btn>
              <v-btn class="primary elevation-1" @click="changeStatus(props.item)">
              {{props.item.status === 'active' ? $lang.actions.deactivate : $lang.actions.activate}}
            </v-btn>
            </td>
          </template>
          <v-alert slot="no-results" :value="true" color="warning" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>
      </v-card>
      <v-dialog v-if="lastOrderModal" v-model="lastOrderModal" max-width="1200">
        <last-order-modal :lastOrder="lastOrder" @close="lastOrderModal = false"/>
      </v-dialog>
      <v-dialog v-if="confirmationModal" v-model="confirmationModal" max-width="500">
        <confirmation-modal :data="confirmationData" @close="confirmationModal = false" @change="deactivate($event)"/>
      </v-dialog>
    </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    components: {
      lastOrderModal: () => import('../modals/LastOrderModal'),
      confirmationModal: () => import('../modals/DeactivationModal'),
    },
    data() {
      return {
        err: '',
        lastOrderModal: false,
        search: '',
        lastOrder: null,
        show: false,
        loading: true,
        companies: [],
        selectedCompany: '',
        companyLoading: true,
        headers: [
          { text: this.$lang.labels.name, value: 'name', align: 'left',},
          { text: this.$lang.labels.externalId, value: 'webShopResourceExternalId', align: 'left',},
          { text: this.$lang.labels.company, value: 'companyName', align: 'left',},
          { text: this.$lang.header.pending, value: 'pendingOrders', align: 'left',},
          { text: this.$lang.header.lastPending, value: 'lastPendingShortOrderId', align: 'left',},
          { text: this.$lang.labels.status, value: 'status', align: 'left',},
          { text: this.$lang.labels.created, value: 'createdOn', align: 'left',},
          { text: this.$lang.labels.actions, value: 'actions', align: 'left', sortable: false,},
        ],
        showActive: true,
        allWebShops: [],
        confirmationModal: false,
        confirmationData: {
          id: null,
          url: 'webshopresource',
        },
        isLocalAdmin: false,
      }
    },
    watch: {
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
      selectedCompany(val) {
        this.fetchWebShopsforCompany(val);
        this.$cookie.set('companyedit', val, { expires: '8h' });
      },
    },
    computed: {
      activeWebShops() {
        const webShops = this.allWebShops;
        const activeWebShops = [];
        for (let i=0; webShops.length > i; i++) {
          if (webShops[i].status === 'active' || webShops[i].status === 'created') {
            activeWebShops.push(webShops[i]);
          }
        }
        return activeWebShops;
      },
      mappedCompanies() {
        return this.companies.map(company => ({
          text: `${company.name} (${company.companyStatus})`,
          value: company.id,
        }));
      },
    },
    methods: {
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
      },
      showOrder(webshop) {
        this.$router.push({ name: 'webShopOrder', params: {id: webshop.webShopResourceExternalId}});
      },
      showModal(webshop) {
        this.lastOrder = {
          id: webshop.lastPendingOrderId,
          short: webshop.lastPendingShortOrderId,
        }
        this.lastOrderModal = true;
      },
      changeStatus(webshop) {
        if (webshop.status === 'inactive' || webshop.status === 'created') {
          this.$http.post(`webshopresource/activate/${webshop.id}`)
          .then((res) => {
            if (res.body.status === 'Success') {
              this.$set(webshop, 'status', 'active');
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
        } else if (webshop.status === "active") {
          this.confirmationData.id = webshop.id;
          this.confirmationModal = true;
        }
      },
      deactivate(id) {
        const result = this.allWebShops.find( data => data.id === id );
        this.$set(result, 'status', 'inactive');
      },
      init() {
        return new Promise((resolve) => {
            this.$http.get('company')
            .then((res) => {
              resolve()
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                if (this.isLocalAdmin) this.selectedCompany = this.mappedCompanies[0].value;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            });
        });
      },
      fetchWebShopsforCompany(id) {
        this.show = true;
        this.allWebShops = [];
        this.$http.post('webshopresource/getall', { companyId: id })
        .then((res) => {
          this.loading = false;
          if (res.body.status === 'Success') {
            this.allWebShops = res.body.details;
            return;
          }
          if (this.allWebShops.length === 0) {
            this.loading = false;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.loading = false;
          this.err = err.body.statusText || err.body.message;
        });
      },
    },
    created() {
      this.isLocalAdmin = this.$auth.roles && this.$auth.roles.includes('ROLE_SUN_LOCAL_ADMIN');
      this.init()
        .then(() => {
          const edit = this.$cookie.get('companyedit');
          if (edit) this.selectedCompany = edit;
        });
    },
  };
</script>

<style lang="scss" scoped>
  .btn--floating.btn--small {
    height: 30px;
    width: 30px;
  }
</style>



// WEBPACK FOOTER //
// WebShop.vue?52cf28a1